import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import save_n from '../../Common/Images/save_n.gif';
import cancel_n from '../../Common/Images/cancel_n.gif';
import close_n from '../../Common/Images/close_n.gif';
import popimg from "../../Common/Images/ImagesForAlert/popup_bg1.png"
import popclose from "../../Common/Images/ImagesForAlert/popup_close_n.png";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface ELPackagesARDProps {
    pkgCode: string | null;
    onClose: () => void;
    onSaveSuccess: () => void;
    showWarning: (msg: string) => void;
    showConfirm: (msg: string, onConfirm: () => void) => void;
}

interface ProductOption {
    ProdID: number;
    ProdDesc: string;
    Subject: string;
    Curriculum: string;
}

const EL_PackagesARD: React.FC<ELPackagesARDProps> = ({ pkgCode, onClose, onSaveSuccess, showWarning, showConfirm }) => {
    const isAddMode = pkgCode === null;
    const [loading, setLoading] = useState(false);
    const [schools, setSchools] = useState<string[]>([]);
    const [currencies, setCurrencies] = useState<string[]>([]);
    const [allProducts, setAllProducts] = useState<ProductOption[]>([]);
    const [formData, setFormData] = useState({
        PackageCode: '',
        SchoolCode: '',
        PackageDescription: '',
        ProdIDs: '',
        ProductNames: '',
        Curriculum: '',
        Subjects: '',
        Currency: '',
        Price: '',
        Price_1: '',
        Price_3: '',
        Price_4: '',
        Price_6: '',
        Price_12: '',
        Price_W: '',
        TrialPeriod: '',
        Status: 'N',
        NoOfMDs: '',
        PackageContains: '',
        YearStart: '0',
        YearEnd: '12',
        AgentCodes: '',
        AgentStates: '',
        AgentPrices: '',
        AgentTimePeriods: ''
    });

    const [isPackageUsed, setIsPackageUsed] = useState(false); // If true, disable SchoolCode
    const [showProductModal, setShowProductModal] = useState(false);
    const [tempSelectedProds, setTempSelectedProds] = useState<Set<number>>(new Set());
    const [isBlocked, setIsBlocked] = useState(false);

    const handleAgentSelectOpen = () => {
        if (!formData.SchoolCode) {
            showWarning("Select a school first.");
            return;
        }

        setIsBlocked(true);

        (window as any).PopupReturnValue = undefined;

        const url = `/WEBADMIN/PRODUCT/EL_SelectAgentCodes?SchoolCode=${encodeURIComponent(formData.SchoolCode)}&Agents=${encodeURIComponent(formData.AgentCodes)}&Price=${encodeURIComponent(formData.AgentPrices)}&tm=${encodeURIComponent(formData.AgentTimePeriods)}`;
        const newWindow = window.open(
            url,
            'SelectAgents',
            'width=600,height=600,top=200,left=200,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=no'
        );

        if (newWindow) {
            const timer = setInterval(() => {
                if (newWindow.closed) {
                    clearInterval(timer);
                    setIsBlocked(false);

                    const retVal = (window as any).PopupReturnValue;
                    if (retVal && typeof retVal === 'string') {
                        const arrProd = retVal.split('|');
                        setFormData(prev => ({
                            ...prev,
                            AgentCodes: arrProd[0] || '',
                            AgentStates: arrProd[1] || '',
                            AgentPrices: arrProd[2] || '',
                            AgentTimePeriods: arrProd[3] || ''
                        }));
                    }
                }
            }, 500);
        } else {
            setIsBlocked(false);
            showWarning("Popup was blocked by your browser. Please allow popups for this site.");
        }
    };

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'AgentPopupReturn' && e.newValue) {
                const retVal = e.newValue;
                const arrProd = retVal.split('|');

                setFormData(prev => ({
                    ...prev,
                    AgentCodes: arrProd[0] || '',
                    AgentStates: arrProd[1] || '',
                    AgentPrices: arrProd[2] || '',
                    AgentTimePeriods: arrProd[3] || ''
                }));

                sessionStorage.removeItem('AgentPopupReturn');
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const schoolsRes = await fetch(`${API_BASE_URL}api/manage-packages/schools/`);
                if (schoolsRes.ok) setSchools(await schoolsRes.json());

                const metaRes = await fetch(`${API_BASE_URL}api/manage-packages/form-data/`);
                if (metaRes.ok) {
                    const metaData = await metaRes.json();
                    setCurrencies(metaData.currencies);
                    setAllProducts(metaData.products);
                }

                if (!isAddMode && pkgCode !== null) {
                    const pkgRes = await fetch(`${API_BASE_URL}api/manage-packages/${pkgCode}/`);
                    if (pkgRes.ok) {
                        const data = await pkgRes.json();

                        let loadedAgentCodes = '';
                        let loadedAgentPrices = '';
                        let loadedAgentTms = '';

                        if (data.Agents && Array.isArray(data.Agents)) {
                            loadedAgentCodes = data.Agents.map((a: any) => a.AgentCode || '').join(',');
                            loadedAgentPrices = data.Agents.map((a: any) => a.Price || '').join(',');
                            loadedAgentTms = data.Agents.map((a: any) => a.TimePeriodInMonths || '').join(',');
                        }

                        setFormData(prev => ({
                            ...prev,
                            ...data,
                            Price: data.Price ?? '',
                            Price_1: data.Price_1 ?? '',
                            Price_3: data.Price_3 ?? '',
                            Price_6: data.Price_6 ?? '',
                            Price_12: data.Price_12 ?? '',
                            Price_W: data.Price_W ?? '',
                            NoOfMDs: data.NoOfMDs ?? '',
                            AgentCodes: loadedAgentCodes,
                            AgentPrices: loadedAgentPrices,
                            AgentTimePeriods: loadedAgentTms,
                            ProductNames: data.ProdIDs || ''
                        }));

                        setIsPackageUsed(data.IsUsed || false);
                    } else {
                        const errData = await pkgRes.json().catch(() => ({}));
                        showWarning(`Failed to load package: ${errData.error || pkgRes.statusText}`);
                        onClose();
                    }
                }
            } catch (e: any) {
                console.error("Initialization error:", e);
                showWarning(`Error loading data: ${e.message || "Unknown error"}`);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [pkgCode]);

    // Helper to resolve names for display in the ReadOnly textarea
    const resolveProductNames = async (idsStr: string, productList: ProductOption[]) => {
        if (!idsStr) return '';
        // If productList is empty (race condition), fetch it again or wait? 
        // For simplicity, we assume productList is populated or we do a quick fetch if needed.
        // In this implementation, we might need to rely on the backend sending names or do a quick lookup.
        // Let's do a quick lookup from the `allProducts` state if available, otherwise just show IDs.
        // *Correction*: To ensure names appear, we might need to wait for product list. 
        // Logic moved to `init` where we wait for metaRes.
        return idsStr;
        // Note: Real mapping happens in the Product Modal 'Save' action. 
        // On initial load, we might just display IDs or fetch a display string from backend if updated.
    };

    const handleProductSelectOpen = () => {
        if (!formData.SchoolCode) {
            showWarning("Select a School first.");
            return;
        }
        // Parse current IDs into Set
        const currentIds = new Set<number>();
        if (formData.ProdIDs) {
            formData.ProdIDs.split(',').forEach(id => {
                if (id.trim()) currentIds.add(parseInt(id.trim()));
            });
        }
        setTempSelectedProds(currentIds);
        setShowProductModal(true);
    };

    const handleProductSelectSave = () => {
        // Logic from legacy GetUniqueValue and string construction
        const selectedList = allProducts.filter(p => tempSelectedProds.has(p.ProdID));

        const ids = selectedList.map(p => p.ProdID).join(',');
        const names = selectedList.map(p => p.ProdDesc).join(', ');

        // Legacy logic: Pick first product's curriculum/subject or merge them?
        // Legacy 'EL_SelectProducts' implies it returns specific strings.
        // We will simple take the first selected product's details for Curriculum/Subject if empty,
        // or just let user edit them as fields are editable in legacy (except when popup returns).

        let newCurriculum = formData.Curriculum;
        let newSubjects = formData.Subjects;

        if (selectedList.length > 0) {
            // Logic: Use the last selected product's metadata to populate fields (mimicking simple selection)
            const last = selectedList[selectedList.length - 1];
            newCurriculum = last.Curriculum;
            newSubjects = last.Subject;
        }

        setFormData(prev => ({
            ...prev,
            ProdIDs: ids,
            ProductNames: names || ids,
            Curriculum: newCurriculum,
            Subjects: newSubjects
        }));
        setShowProductModal(false);
    };

    const handleSave = async () => {
        // Validation (fn_Validate logic)
        if (!formData.PackageCode) { showWarning("Package Code cannot be empty"); return; }
        if (isNaN(Number(formData.PackageCode))) { showWarning("Package code must contain numbers only."); return; }
        if (!formData.PackageDescription) { showWarning("Package Description cannot be empty"); return; }
        if (!formData.SchoolCode) { showWarning("Select a School"); return; }
        const prices = [
            formData.Price,
            formData.Price_1,
            formData.Price_3,
            formData.Price_6,
            formData.Price_12,
            formData.Price_W
        ];

        if (prices.every(p => !p || String(p).trim() === '')) {
            showWarning("Please specify at least 1 price");
            return;
        }

        const isNumeric = (val: string | number) => !val || !isNaN(Number(val));
        if (!isNumeric(formData.Price)) { showWarning("List Price must be numeric"); return; }

        const payload = {
            ...formData,
            YearStart: formData.YearStart || 0,
            YearEnd: formData.YearEnd || 12,
        };

        setLoading(true);
        try {
            const url = isAddMode ? `${API_BASE_URL}api/manage-packages/` : `${API_BASE_URL}api/manage-packages/${pkgCode}/`;
            const method = isAddMode ? 'POST' : 'PUT';

            const response = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const res = await response.json();
            if (response.ok) {
                onSaveSuccess();
                onClose();
            } else {
                showWarning(res.error || "Error saving package.");
            }
        } catch (e) {
            showWarning("Network error.");
        } finally {
            setLoading(false);
        }
    };

    // Styles matching legacy
    const styles = {
        overlay: {
            position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center'
        },
        container: {
            backgroundColor: '#E8E8FF',
            width: '100%',
            height: '100%',
            overflowY: 'auto' as const,
            border: 'none',
            padding: '5px',
            fontFamily: 'Arial',
            fontSize: '10pt'
        },
        header: {
            backgroundColor: 'Maroon', color: 'White', fontWeight: 'bold' as const, textAlign: 'center' as const,
            padding: '5px', fontSize: '12pt', marginBottom: '10px'
        },
        table: { width: '100%', borderCollapse: 'collapse' as const },
        tdLabel: { width: '25%', padding: '4px' },
        tdInput: { width: '75%', padding: '4px' },
        input: { width: '98%', border: '1px solid #7F9DB9', padding: '2px' },
        readOnly: { backgroundColor: '#f0f0f0' },
        link: { color: 'Blue', textDecoration: 'underline', cursor: 'pointer' },
        footer: { textAlign: 'center' as const, marginTop: '15px' },
        imgBtn: { cursor: 'pointer', margin: '0 10px' },
        topHeader: {
            backgroundImage: `url(${popimg})`,
            backgroundRepeat: 'repeat-x',
            height: '35px',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            padding: 0,
            margin: 0
        },
        closeBtn: {
            cursor: 'pointer',
            display: 'block'
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.container}>
                <div style={styles.topHeader}>
                    <div style={styles.topHeader}>
                        <img
                            src={popclose}
                            alt="Close"
                            style={styles.closeBtn}
                            onClick={onClose}
                        />
                    </div>
                </div>
                <div style={styles.header}>
                    {isAddMode ? 'Add a Package' : 'Modification of Package'}
                </div>

                {loading && <Loading />}

                <table style={styles.table}>
                    <tbody>
                        <tr>
                            <td style={styles.tdLabel}>Package Code</td>
                            <td style={styles.tdInput}>
                                <input
                                    type="text"
                                    style={{ ...styles.input, backgroundColor: !isAddMode ? '#f0f0f0' : 'white' }}
                                    maxLength={4}
                                    value={formData.PackageCode}
                                    disabled={!isAddMode}
                                    onChange={e => setFormData({ ...formData, PackageCode: e.target.value })}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>Package Description</td>
                            <td style={styles.tdInput}>
                                <input
                                    type="text"
                                    style={styles.input}
                                    value={formData.PackageDescription}
                                    onChange={e => setFormData({ ...formData, PackageDescription: e.target.value })}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>eDealer Code</td>
                            <td style={styles.tdInput}>
                                <select
                                    style={styles.input}
                                    value={formData.SchoolCode}
                                    disabled={isPackageUsed}
                                    onChange={e => setFormData({ ...formData, SchoolCode: e.target.value })}
                                >
                                    <option value="">---- Select School ----</option>
                                    {schools.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>
                                <span style={styles.link} onClick={handleProductSelectOpen}>Product(s)</span>
                            </td>
                            <td style={styles.tdInput}>
                                <textarea
                                    rows={3}
                                    style={{ ...styles.input, ...styles.readOnly }}
                                    readOnly
                                    value={formData.ProductNames || formData.ProdIDs}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>Curriculum</td>
                            <td style={styles.tdInput}>
                                <input
                                    type="text"
                                    style={styles.input}
                                    value={formData.Curriculum}
                                    onChange={e => setFormData({ ...formData, Curriculum: e.target.value })}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>Subject(s)</td>
                            <td style={styles.tdInput}>
                                <input
                                    type="text"
                                    style={{ ...styles.input, ...styles.readOnly }}
                                    readOnly
                                    value={formData.Subjects}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>Currency</td>
                            <td style={styles.tdInput}>
                                <select
                                    style={styles.input}
                                    value={formData.Currency}
                                    onChange={e => setFormData({ ...formData, Currency: e.target.value })}
                                >
                                    <option value="">-- Select --</option>
                                    {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>List Price</td>
                            <td style={styles.tdInput}>
                                <input
                                    type="text"
                                    style={{ ...styles.input, width: '20%' }}
                                    value={formData.Price}
                                    onChange={e => setFormData({ ...formData, Price: e.target.value })}
                                />
                            </td>
                        </tr>
                        {/* Subscription Prices Row 1 */}
                        <tr>
                            <td style={styles.tdLabel}>Subscription</td>
                            <td style={styles.tdInput}>
                                <table style={{ width: '100%' }}>
                                    <tbody>
                                        <tr>
                                            <td width="20%">For 1 Month:</td>
                                            <td width="30%">
                                                <input type="text" style={styles.input} value={formData.Price_1} onChange={e => setFormData({ ...formData, Price_1: e.target.value })} />
                                            </td>
                                            <td width="20%" align="right">For 3 months:&nbsp;</td>
                                            <td width="30%">
                                                <input type="text" style={styles.input} value={formData.Price_3} onChange={e => setFormData({ ...formData, Price_3: e.target.value })} />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        {/* Subscription Prices Row 2 */}
                        <tr>
                            <td style={styles.tdLabel}></td>
                            <td style={styles.tdInput}>
                                <table style={{ width: '100%' }}>
                                    <tbody>
                                        <tr>
                                            <td width="20%">For 6 Months:</td>
                                            <td width="30%">
                                                <input type="text" style={styles.input} value={formData.Price_6} onChange={e => setFormData({ ...formData, Price_6: e.target.value })} />
                                            </td>
                                            <td width="20%" align="right">For 1 Year:&nbsp;</td>
                                            <td width="30%">
                                                <input type="text" style={styles.input} value={formData.Price_12} onChange={e => setFormData({ ...formData, Price_12: e.target.value })} />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        {/* Subscription Prices Row 3 */}
                        <tr>
                            <td style={styles.tdLabel}></td>
                            <td style={styles.tdInput}>
                                <table style={{ width: '100%' }}>
                                    <tbody>
                                        <tr>
                                            <td width="20%">Weekly:</td>
                                            <td width="30%">
                                                <input type="text" style={styles.input} value={formData.Price_W} onChange={e => setFormData({ ...formData, Price_W: e.target.value })} />
                                            </td>
                                            <td></td><td></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>Trial Period</td>
                            <td style={styles.tdInput}>
                                <input type="text" style={styles.input} value={formData.TrialPeriod} onChange={e => setFormData({ ...formData, TrialPeriod: e.target.value })} />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>Status</td>
                            <td style={styles.tdInput}>
                                <select style={styles.input} value={formData.Status} onChange={e => setFormData({ ...formData, Status: e.target.value })}>
                                    <option value="A">Active</option>
                                    <option value="N">Inactive</option>
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>No. of MD Licenses</td>
                            <td style={styles.tdInput}>
                                <input type="text" style={styles.input} value={formData.NoOfMDs} onChange={e => setFormData({ ...formData, NoOfMDs: e.target.value })} />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel} valign="top">Package Includes:</td>
                            <td style={styles.tdInput}>
                                <textarea rows={3} style={styles.input} value={formData.PackageContains} onChange={e => setFormData({ ...formData, PackageContains: e.target.value })} />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>Year</td>
                            <td style={styles.tdInput}>
                                Start: <input type="text" style={{ ...styles.input, width: '60px' }} value={formData.YearStart} onChange={e => setFormData({ ...formData, YearStart: e.target.value })} />
                                &nbsp;&nbsp;
                                End: <input type="text" style={{ ...styles.input, width: '60px' }} value={formData.YearEnd} onChange={e => setFormData({ ...formData, YearEnd: e.target.value })} />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>
                                <span style={styles.link} onClick={handleAgentSelectOpen}> Sale Agent(s)</span>
                            </td>
                            <td style={styles.tdInput}>
                                <textarea
                                    rows={3}
                                    style={{ ...styles.input, ...styles.readOnly }}
                                    readOnly
                                    value={formData.AgentCodes}
                                    placeholder=""
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style={styles.footer}>
                    <img src={save_n} alt="Save" style={styles.imgBtn} onClick={handleSave} />
                    <img src={cancel_n} alt="Close" style={styles.imgBtn} onClick={onClose} />
                </div>
            </div>

            {/* Product Selection Modal */}
            {showProductModal && (
                <div style={{ ...styles.overlay, zIndex: 2100 }}>
                    <div style={{ backgroundColor: 'white', width: '500px', height: '600px', border: '1px solid black', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ backgroundColor: '#C7DDFD', padding: '10px', fontWeight: 'bold', borderBottom: '1px solid gray' }}>
                            Select Products
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
                            {allProducts.map(p => (
                                <div key={p.ProdID} style={{ padding: '5px', borderBottom: '1px solid #80a0ff' }}>
                                    <input
                                        type="checkbox"
                                        checked={tempSelectedProds.has(p.ProdID)}
                                        onChange={() => {
                                            const newSet = new Set(tempSelectedProds);
                                            if (newSet.has(p.ProdID)) newSet.delete(p.ProdID);
                                            else newSet.add(p.ProdID);
                                            setTempSelectedProds(newSet);
                                        }}
                                    />
                                    &nbsp; {p.ProdID} - {p.ProdDesc} ({p.Subject})
                                </div>
                            ))}
                        </div>
                        <div style={{ padding: '10px', textAlign: 'center', borderTop: '1px solid gray' }}>
                            <button onClick={handleProductSelectSave} style={{ width: '80px', marginRight: '10px' }}>OK</button>
                            <button onClick={() => setShowProductModal(false)} style={{ width: '80px' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            {isBlocked && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(204, 204, 204, 0.6)', zIndex: 9999, cursor: 'wait'
                }}></div>
            )}
        </div>
    );
}

export default EL_PackagesARD;